/* Finder — "the sentence" (Direction C) — progressive-enhancement layer only.
   Every slot option / preset / finishing touch / stat pill is a real, server-rendered
   <a href="/collections/..."> and the popovers are native <details>/<summary> — so the whole
   thing already works with this script disabled (JS-off: clicking any option just navigates
   to its curated collection). This file only adds:
     - one-popover-open-at-a-time + right-edge overflow alignment,
     - Esc closes the open popover and returns focus to its slot button,
     - intercepting slot-option / preset clicks to compose the visible sentence + a single
       "See these games" link instead of navigating immediately,
     - a lightweight "Surprise me" randomizer over the currently-rendered curated links.
   Tag-path mode (data-finder-base, BG-105 r7e) adds a live match count: one debounced request
   per new combination to the section-rendering endpoint (?sections=finder-count), cached.
   Full APG listbox arrow-key navigation is out of scope for this shell (Tab reaches every
   option in natural DOM order, which is sufficient here); note left for the future full build. */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.finder-sentence.section';
  // Phones get the bottom-sheet pickers (the CSS breakpoint is the same 640px).
  var PHONE = window.matchMedia('(max-width: 640px)');

  // Syncs aria-expanded (and the overflow-alignment class) to the details element's ACTUAL
  // open state — called both for JS-driven open/close and for a native close (re-clicking the
  // <summary>, which the browser toggles without going through closeSlot/openSlot).
  function syncAriaExpanded(slot) {
    var summary = slot.querySelector('[data-slot]');
    if (summary) summary.setAttribute('aria-expanded', slot.open ? 'true' : 'false');
    if (!slot.open) {
      var pop = slot.querySelector('.finder-pop');
      if (pop) pop.classList.remove('pop--end');
    }
    var root = slot.closest(ROOT_SELECTOR);
    if (root) syncScrim(root);
  }

  // The scrim shows only while a phone sheet is open (it is hidden on wider screens).
  function syncScrim(root) {
    var scrim = root.querySelector('[data-finder-scrim]');
    if (scrim) scrim.hidden = !(PHONE.matches && root.querySelector('.finder-slot[open]'));
  }

  function closeSlot(slot, refocus) {
    if (!slot.open) return;
    slot.open = false;
    syncAriaExpanded(slot);
    if (refocus) {
      var summary = slot.querySelector('[data-slot]');
      if (summary) summary.focus();
    }
  }

  function openSlot(slot) {
    var root = slot.closest(ROOT_SELECTOR);
    if (root) {
      var others = root.querySelectorAll('.finder-slot[open]');
      for (var i = 0; i < others.length; i++) {
        if (others[i] !== slot) closeSlot(others[i]);
      }
    }
    syncAriaExpanded(slot);
    var pop = slot.querySelector('.finder-pop');
    if (pop && pop.getBoundingClientRect().right > document.documentElement.clientWidth - 8) {
      pop.classList.add('pop--end');
      if (pop.getBoundingClientRect().left < 8) pop.classList.remove('pop--end');
    }
    if (PHONE.matches && pop) {
      // Focus comes from outside the sheet (the summary, a bento link), so the focusout close below ignores it.
      var opt = pop.querySelector('.fchip[aria-current="true"]') || pop.querySelector('.fchip');
      if (opt) {
        opt.focus({ preventScroll: true });
        // Scroll the sheet, not the page, so the focused option is visible on a short phone.
        var top = opt.getBoundingClientRect().top - pop.getBoundingClientRect().top + pop.scrollTop;
        if (top < pop.scrollTop || top + opt.offsetHeight > pop.scrollTop + pop.clientHeight) pop.scrollTop = Math.max(0, top - (pop.clientHeight - opt.offsetHeight) / 2);
      }
    }
  }

  function ctaLabel(root) {
    var bar = root.querySelector('[data-finder-bar]');
    return (bar && bar.getAttribute('data-cta-label')) || 'See these games';
  }

  function resetIntro(root) {
    var bar = root.querySelector('[data-finder-bar]');
    var count = root.querySelector('.finder-count');
    if (!bar || !count) return;
    count.textContent = bar.getAttribute('data-intro-text') || '';
    hideCta(root);
  }

  // Builds "You picked <b>label</b>." via DOM nodes (never innerHTML with untrusted text) inside
  // the section's one aria-live region; the CTA is the server-rendered button in the bar. Not used in tag-path mode.
  function setIntro(root, label, href, cta) {
    var count = root.querySelector('.finder-count');
    if (!count || !href) return;
    count.textContent = '';
    count.appendChild(document.createTextNode('You picked '));
    var b = document.createElement('b');
    b.textContent = label;
    count.appendChild(b);
    count.appendChild(document.createTextNode('.'));
    setCta(root, href, cta);
  }

  // Task C4 — builds the multi-slot deep link from every currently-picked slot chip. Params
  // come straight from the server-rendered data-finder-params attribute (itself pasted verbatim
  // from finder-page-plan.md by the merchant into the block setting) — this function never
  // invents or types a param value, it only joins the ones already on the page. A chip with no
  // finder_params configured still contributes its label to the composed sentence but nothing
  // to the querystring — an honest partial deep link, never a broken one.
  function composeDeepLink(root, finderUrl) {
    var activeChips = root.querySelectorAll('.finder-pop .fchip[aria-current="true"]');
    if (!activeChips.length) return null;
    var params = [];
    var labels = [];
    for (var i = 0; i < activeChips.length; i++) {
      var p = activeChips[i].getAttribute('data-finder-params');
      if (p) params.push(p);
      labels.push(activeChips[i].textContent.trim());
    }
    if (!params.length) {
      // [REV] Doorway honesty: nothing among the active pick(s) is actually wired to filter the
      // finder page — `finderUrl` with no querystring would silently show EVERY game while the
      // sentence/CTA claim the pick was applied. With exactly one active (unwired) pick, that
      // chip's own `href` is already the honest destination — finder-sentence.liquid resolves it
      // server-side to that chip's curated collection whenever finder_params is blank (see
      // opt_href there), the same link JS-off delivers. With two+ unwired picks active at once
      // there's no single collection that honestly represents both, so (like the "no active
      // chips" case above) offer no CTA rather than guess.
      if (activeChips.length === 1) {
        var soleHref = activeChips[0].getAttribute('href');
        if (soleHref) return { href: soleHref, label: labels.join(', ') };
      }
      return null;
    }
    var href = finderUrl + '?' + params.join('&');
    return { href: href, label: labels.join(', ') };
  }

  // BG-105 R7c — tag-path mode (data-finder-base). ONE selection state: at most one selected
  // chip per slot; words shown and the CTA's tags both come only from the selected chips.
  var FACETS = ['players', 'depth', 'time'];

  function selectedChip(root, facet) {
    return root.querySelector('.finder-slot[data-facet="' + facet + '"] .fchip[aria-current="true"]');
  }

  function composeBaseLink(root, base) {
    var tags = [];
    var labels = [];
    for (var i = 0; i < FACETS.length; i++) {
      var chip = selectedChip(root, FACETS[i]);
      if (!chip) continue;
      labels.push(chip.textContent.trim());
      var t = chip.getAttribute('data-finder-tags');
      if (t) tags.push(t);
    }
    if (!tags.length) return null;
    return { href: base.replace(/\/$/, '') + '/' + tags.join('+'), label: labels.join(', ') };
  }

  // A composer preset is current only while the selection equals exactly its words.
  function syncPresets(root) {
    var presets = root.querySelectorAll('.preset[data-players-word]');
    for (var i = 0; i < presets.length; i++) {
      var match = true;
      var named = false;
      for (var f = 0; f < FACETS.length; f++) {
        var w = presets[i].getAttribute('data-' + FACETS[f] + '-word');
        var chip = selectedChip(root, FACETS[f]);
        if (w) named = true;
        if (w ? !(chip && chip.getAttribute('data-word') === w) : !!chip) match = false;
      }
      presets[i].setAttribute('aria-current', named && match ? 'true' : 'false');
    }
  }

  // Live count (tag-path mode). Per-root state: debounce timer, abort controller + 4 s timeout,
  // a request id that guards every late paint, and a cache of SUCCESSFUL replies only.
  function state(root) {
    return root.__fs || (root.__fs = { timer: 0, tmo: 0, ctrl: null, reqId: 0, cache: new Map() });
  }

  function cancelPending(root) {
    var st = state(root);
    clearTimeout(st.timer);
    clearTimeout(st.tmo);
    if (st.ctrl) st.ctrl.abort();
    st.ctrl = null;
    st.reqId++;
    var count = root.querySelector('.finder-count');
    if (count) count.removeAttribute('aria-busy');
  }

  function barAttr(root, name) {
    var bar = root.querySelector('[data-finder-bar]');
    return (bar && bar.getAttribute(name)) || '';
  }

  // Appends a template with every [count] replaced by a bold, formatted number (DOM nodes only).
  function appendTpl(parent, tpl, num) {
    var parts = tpl.split('[count]');
    for (var i = 0; i < parts.length; i++) {
      if (i) {
        var b = document.createElement('b');
        b.textContent = num;
        parent.appendChild(b);
      }
      if (parts[i]) parent.appendChild(document.createTextNode(parts[i]));
    }
  }

  // The noun for the zero state: the slot the shopper touched last, or else the last selected one.
  function zeroNoun(root) {
    var f = root.__lastFacet;
    if (!f || !selectedChip(root, f)) {
      f = '';
      for (var i = 0; i < FACETS.length; i++) if (selectedChip(root, FACETS[i])) f = FACETS[i];
    }
    var slot = f && root.querySelector('.finder-slot[data-facet="' + f + '"]');
    return (slot && slot.getAttribute('data-noun')) || '';
  }

  function ctaEl(root) { return root.querySelector('[data-finder-cta]'); }

  // The link is server-rendered and updated in place, so keyboard focus on it survives a repaint.
  function setCta(root, href, text) {
    var a = ctaEl(root);
    if (!a) return;
    a.href = href;
    var t = a.querySelector('[data-finder-cta-text]');
    if (t) t.textContent = text;
    a.hidden = false;
  }

  function hideCta(root) {
    var a = ctaEl(root);
    if (a) a.hidden = true;
  }

  function paintCount(root, n, href) {
    var count = root.querySelector('.finder-count');
    if (!count) return;
    var num = n.toLocaleString('en-CA');
    if (n === 0) {
      var cta = ctaEl(root);
      var focused = !!cta && cta.contains(cta.ownerDocument.activeElement);
      hideCta(root);
      count.textContent = barAttr(root, 'data-zero-text').replace('[slot]', zeroNoun(root));
      if (focused) {
        var clr = root.querySelector('[data-finder-clear]');
        if (clr) clr.focus();
      }
      return;
    }
    // The button carries the number; the optional count sentence is shown only when its setting is set.
    var tpl = barAttr(root, n === 1 ? 'data-count-one' : 'data-count-text');
    count.textContent = '';
    if (tpl) appendTpl(count, tpl, num);
    setCta(root, href, barAttr(root, n === 1 ? 'data-cta-one' : 'data-cta-count').replace('[count]', num));
  }

  function paintFallback(root, href) {
    var count = root.querySelector('.finder-count');
    if (count) count.textContent = '';
    setCta(root, href, ctaLabel(root));
  }

  function renderBase(root, base) {
    cancelPending(root);
    var composed = composeBaseLink(root, base);
    if (!composed) {
      resetIntro(root);
      hideClear(root);
      return;
    }
    var st = state(root);
    var href = composed.href;
    showClear(root);
    if (st.cache.has(href)) {
      paintCount(root, st.cache.get(href), href);
      return;
    }
    paintFallback(root, href);
    if (!window.fetch || !window.AbortController) return;
    var count = root.querySelector('.finder-count');
    if (count) count.setAttribute('aria-busy', 'true');
    var id = st.reqId;
    function done() {
      if (id !== st.reqId) return;
      clearTimeout(st.tmo);
      if (count) count.removeAttribute('aria-busy');
    }
    st.timer = setTimeout(function () {
      var ctrl = new AbortController();
      st.ctrl = ctrl;
      st.tmo = setTimeout(function () { ctrl.abort(); }, 4000);
      // Shopify 301s a multi-tag path to alphabetical tag order, so ask for that order directly
      // (the CTA href and the cache key stay as composed).
      var cut = href.lastIndexOf('/') + 1;
      var url = href.slice(0, cut) + href.slice(cut).split('+').sort().join('+') + '?sections=finder-count';
      fetch(url, { signal: ctrl.signal })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (j) {
          if (id !== st.reqId) return;
          var m = /data-finder-count="(\d+)"/.exec((j && j['finder-count']) || '');
          if (m) {
            var n = parseInt(m[1], 10);
            st.cache.set(href, n);
            paintCount(root, n, href);
          }
          done();
        })
        .catch(done);
    }, 200);
  }

  // Marks each question row that holds a pick (the phone rows colour the word by it).
  function syncSet(root) {
    var slots = root.querySelectorAll('.finder-slot');
    for (var i = 0; i < slots.length; i++) slots[i].classList.toggle('is-set', !!slots[i].querySelector('.fchip[aria-current="true"]'));
  }

  // Every pick change ends in showClear or hideClear, so is-set is synced here.
  function showClear(root) {
    var btn = root.querySelector('[data-finder-clear]');
    if (btn) btn.hidden = false;
    syncSet(root);
  }

  function hideClear(root) {
    var btn = root.querySelector('[data-finder-clear]');
    if (btn) btn.hidden = true;
    syncSet(root);
  }

  function clearAll(root) {
    var chips = root.querySelectorAll('.fchip[aria-current="true"], .preset[aria-current="true"]');
    for (var i = 0; i < chips.length; i++) chips[i].setAttribute('aria-current', 'false');
    var slots = root.querySelectorAll('.finder-slot');
    for (var j = 0; j < slots.length; j++) {
      closeSlot(slots[j]);
      var word = slots[j].querySelector('[data-slot-word]');
      if (word) word.textContent = word.getAttribute('data-default') || word.textContent;
    }
    cancelPending(root);
    root.__lastFacet = null;
    resetIntro(root);
    hideClear(root);
  }

  // <details>/toggle is non-bubbling — a capture-phase document listener still sees it,
  // because capture runs top-down before bubbling would matter. Fires for BOTH open and
  // close (including a native close via re-clicking/re-activating the <summary>, which
  // never goes through openSlot/closeSlot) — sync aria-expanded either way so it never
  // sticks "true" after a native close.
  document.addEventListener('toggle', function (e) {
    var slot = e.target && e.target.closest && e.target.closest('.finder-slot');
    if (!slot) return;
    if (slot.open) {
      openSlot(slot);
    } else {
      syncAriaExpanded(slot);
    }
  }, true);

  // Phones: focus leaving the open sheet, to its own summary or anywhere else, closes it so focus never sits under the sheet.
  document.addEventListener('focusout', function (e) {
    if (!PHONE.matches || !e.relatedTarget) return;
    var pop = e.target.closest && e.target.closest('.finder-slot[open] .finder-pop');
    if (pop && !pop.contains(e.relatedTarget)) closeSlot(pop.closest('.finder-slot'));
  });

  // Crossing 640px with a slot open: close it (focus back on its summary if it was inside) so a scrim never outlives the sheet.
  function onPhoneChange() {
    var open = document.querySelectorAll('.finder-slot[open]');
    for (var i = 0; i < open.length; i++) closeSlot(open[i], open[i].contains(document.activeElement));
  }
  if (PHONE.addEventListener) PHONE.addEventListener('change', onPhoneChange);
  else if (PHONE.addListener) PHONE.addListener(onPhoneChange);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = document.querySelector('.finder-slot[open]');
    if (open) closeSlot(open, true);
  });

  document.addEventListener('click', function (e) {
    var insideSlot = e.target.closest('.finder-slot');
    var openSlots = document.querySelectorAll('.finder-slot[open]');
    var onScrim = !!e.target.closest('[data-finder-scrim]');
    for (var i = 0; i < openSlots.length; i++) {
      if (openSlots[i] !== insideSlot) closeSlot(openSlots[i], onScrim);
    }

    var sheetX = e.target.closest('[data-finder-sheet-close]');
    if (sheetX) {
      var xSlot = sheetX.closest('.finder-slot');
      if (xSlot) closeSlot(xSlot, true);
      return;
    }

    var ideasBtn = e.target.closest('[data-finder-ideas-toggle]');
    if (ideasBtn) {
      var card = ideasBtn.closest('.fs-ideas');
      if (card) ideasBtn.setAttribute('aria-expanded', card.classList.toggle('is-open') ? 'true' : 'false');
      return;
    }

    var chip = e.target.closest('.finder-pop .fchip');
    if (chip) {
      e.preventDefault();
      var slot = chip.closest('.finder-slot');
      var root = chip.closest(ROOT_SELECTOR);
      if (!slot || !root) return;
      // Task C4 — the finder-page doorway. When a "Finder page URL" is configured
      // (data-finder-url, rendered from section.settings.finder_url), a pick in ONE slot no
      // longer resets the others: multiple slots can now compose into a single deep link on the
      // real finder page, which actually delivers a multi-facet combination (unlike the old
      // single-curated-collection model, where "date night that's a brain-burner" had nowhere
      // real to go). With finder_url blank, behaviour below is byte-identical to before this
      // task — see composeDeepLink's early return and the `else` branches.
      var finderUrl = root.getAttribute('data-finder-url');
      var baseUrl = root.getAttribute('data-finder-base');
      var deepLinkMode = !!finderUrl || !!baseUrl;
      var was = chip.getAttribute('aria-current') === 'true';
      var siblings = slot.querySelectorAll('.fchip[aria-current="true"]');
      for (var s = 0; s < siblings.length; s++) siblings[s].setAttribute('aria-current', 'false');
      var word = slot.querySelector('[data-slot-word]');
      // Re-picking the active option clears that facet (matches the reference mockup's contract).
      if (!was) {
        chip.setAttribute('aria-current', 'true');
        if (word) word.textContent = chip.getAttribute('data-word') || '';
        if (!deepLinkMode) {
          // Reset-on-pick: there is no live intersection filter yet, and the CTA is last-pick-wins
          // (one collection), so a pick in one slot resets every OTHER slot to its default word —
          // otherwise the sentence could promise a multi-facet combination ("date night that's a
          // brain-burner") the single "See these games" link doesn't actually deliver.
          var otherSlots = root.querySelectorAll('.finder-slot');
          for (var os = 0; os < otherSlots.length; os++) {
            if (otherSlots[os] === slot) continue;
            var otherWord = otherSlots[os].querySelector('[data-slot-word]');
            if (otherWord) otherWord.textContent = otherWord.getAttribute('data-default') || '';
            var otherChips = otherSlots[os].querySelectorAll('.fchip[aria-current="true"]');
            for (var oc = 0; oc < otherChips.length; oc++) otherChips[oc].setAttribute('aria-current', 'false');
            closeSlot(otherSlots[os]);
          }
        }
        // deep-link mode: other slots' selections are left intact — see composeDeepLink below.
      } else if (word) {
        word.textContent = word.getAttribute('data-default') || '';
      }
      // A slot pick no longer matches any single preset combination exactly — clear preset state
      // (tag-path mode re-derives it from the selection instead, see syncPresets).
      var presets = root.querySelectorAll('.preset[aria-current="true"]');
      for (var p = 0; p < presets.length; p++) presets[p].setAttribute('aria-current', 'false');
      closeSlot(slot, true);

      if (baseUrl) {
        root.__lastFacet = slot.getAttribute('data-facet');
        syncPresets(root);
        renderBase(root, baseUrl);
      } else if (deepLinkMode) {
        var composed = composeDeepLink(root, finderUrl);
        if (composed) {
          setIntro(root, composed.label, composed.href, ctaLabel(root));
          showClear(root);
        } else {
          resetIntro(root);
          hideClear(root);
        }
      } else if (was) {
        resetIntro(root);
        hideClear(root);
      } else {
        setIntro(root, chip.textContent.trim(), chip.getAttribute('href'), ctaLabel(root));
        showClear(root);
      }
      return;
    }

    var preset = e.target.closest('.preset[data-players-word]');
    if (preset) {
      var root2 = preset.closest(ROOT_SELECTOR);
      if (!root2) return;
      var baseUrl2 = root2.getAttribute('data-finder-base');
      if (baseUrl2) {
        // Link preset (no words): a plain link, never intercepted.
        if (!preset.getAttribute('data-players-word') && !preset.getAttribute('data-depth-word') && !preset.getAttribute('data-time-word')) return;
        e.preventDefault();
        var wasCur = preset.getAttribute('aria-current') === 'true';
        root2.__lastFacet = null;
        for (var bf = 0; bf < FACETS.length; bf++) {
          var bslot = root2.querySelector('.finder-slot[data-facet="' + FACETS[bf] + '"]');
          if (!bslot) continue;
          var bword = bslot.querySelector('[data-slot-word]');
          var pw = wasCur ? '' : preset.getAttribute('data-' + FACETS[bf] + '-word');
          if (pw) root2.__lastFacet = FACETS[bf];
          var shown = '';
          var bchips = bslot.querySelectorAll('.fchip');
          for (var bc = 0; bc < bchips.length; bc++) {
            var on = !!pw && bchips[bc].getAttribute('data-word') === pw;
            bchips[bc].setAttribute('aria-current', on ? 'true' : 'false');
            if (on) shown = pw;
          }
          if (bword) bword.textContent = shown || bword.getAttribute('data-default') || '';
          closeSlot(bslot);
        }
        syncPresets(root2);
        renderBase(root2, baseUrl2);
        return;
      }
      e.preventDefault();
      var wasOn = preset.getAttribute('aria-current') === 'true';
      var otherPresets = root2.querySelectorAll('.preset[aria-current="true"]');
      for (var op = 0; op < otherPresets.length; op++) otherPresets[op].setAttribute('aria-current', 'false');

      var facets = ['players', 'depth', 'time'];
      for (var f = 0; f < facets.length; f++) {
        var slotEl = root2.querySelector('.finder-slot[data-facet="' + facets[f] + '"]');
        if (!slotEl) continue;
        var wordEl = slotEl.querySelector('[data-slot-word]');
        var presetWord = preset.getAttribute('data-' + facets[f] + '-word');
        if (wordEl) {
          wordEl.textContent = (!wasOn && presetWord) ? presetWord : (wordEl.getAttribute('data-default') || '');
        }
        closeSlot(slotEl);
        var slotChips = slotEl.querySelectorAll('.fchip[aria-current="true"]');
        for (var c = 0; c < slotChips.length; c++) slotChips[c].setAttribute('aria-current', 'false');
      }

      if (wasOn) {
        resetIntro(root2);
        hideClear(root2);
      } else {
        preset.setAttribute('aria-current', 'true');
        setIntro(root2, preset.textContent.trim(), preset.getAttribute('href'), ctaLabel(root2));
        showClear(root2);
      }
      return;
    }

    var clearBtn = e.target.closest('[data-finder-clear]');
    if (clearBtn) {
      var root3 = clearBtn.closest(ROOT_SELECTOR);
      if (root3) clearAll(root3);
    }
  });

  // Surprise me: real link, works with JS off. With JS, pick a random curated link that is
  // actually rendered on the page right now and swap it in before the browser navigates —
  // it never invents a destination.
  document.addEventListener('click', function (e) {
    var surprise = e.target.closest('[data-finder-surprise]');
    if (!surprise) return;
    var root = surprise.closest(ROOT_SELECTOR);
    if (!root) return;
    var candidates = root.querySelectorAll('.finder-pop .fchip[href], .preset[href][data-players-word]');
    if (!candidates.length) return;
    var pick = candidates[Math.floor(Math.random() * candidates.length)];
    var href = pick.getAttribute('href');
    if (href) surprise.setAttribute('href', href);
  });
  // BG-105 r7e: "Change picks" on a results page links to /#finder=<tags>. Select, per facet, the
  // chip whose tags are all in the hash (the most tags wins); leftovers are ignored. Then bring
  // the finder into view and focus its first selected (or the players) slot. Base mode only.
  // BG-31: openFacet (optional) opens that slot; smooth is set only for in-page jumps, so the load
  // path keeps its instant jump. A non-empty payload REPLACES each facet's pick (Back to an older
  // #finder=<tags> must not leave two chips current); on a fresh page no chip is current yet, so the
  // load path selects exactly what it did before. A bare #finder changes no picks.
  function restoreFromHash(openFacet, smooth) {
    var m = /^#finder(?:=(.*))?$/.exec(location.hash);
    var root = m && document.querySelector(ROOT_SELECTOR + '[data-finder-base]');
    if (!root) return;
    var want = {};
    var payload = false;
    // Validate the whole payload first: a malformed one selects nothing (it still scrolls/focuses).
    if (/^[a-z0-9+-]+$/.test(m[1] || '')) m[1].split('+').forEach(function (t) { if (t) { want[t] = 1; payload = true; } });
    var focusSlot = null;
    var cleared = false;
    for (var f = 0; f < FACETS.length; f++) {
      var slot = root.querySelector('.finder-slot[data-facet="' + FACETS[f] + '"]');
      if (!slot) continue;
      var chips = slot.querySelectorAll('.fchip[data-finder-tags]');
      var best = null;
      var bestN = 0;
      for (var c = 0; c < chips.length; c++) {
        var tags = chips[c].getAttribute('data-finder-tags').split('+');
        if (tags.length > bestN && tags.every(function (t) { return want[t]; })) {
          best = chips[c];
          bestN = tags.length;
        }
      }
      var word = slot.querySelector('[data-slot-word]');
      if (payload) {
        var cur = slot.querySelectorAll('.fchip[aria-current="true"]');
        for (var k = 0; k < cur.length; k++) {
          if (cur[k] === best) continue;
          cur[k].setAttribute('aria-current', 'false');
          cleared = true;
          if (!best && word) word.textContent = word.getAttribute('data-default') || '';
        }
      }
      if (!best) continue;
      best.setAttribute('aria-current', 'true');
      if (word) word.textContent = best.getAttribute('data-word') || '';
      root.__lastFacet = FACETS[f];
      focusSlot = focusSlot || slot;
    }
    if (focusSlot || cleared) {
      syncPresets(root);
      renderBase(root, root.getAttribute('data-finder-base'));
    }
    var open = FACETS.indexOf(openFacet) > -1 ? root.querySelector('.finder-slot[data-facet="' + openFacet + '"]') : null;
    if (open) { open.open = true; openSlot(open); }
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // main.js setHeaderHeight() looks up #shopify-section-header and misses this theme's id, so --header-height is unset at 768px and narrower: use the sticky header's height instead (its bottom edge moves with scroll; the browser's own #finder fragment scroll after load needs the same margin on that element).
    var hdr = document.querySelector('store-header');
    var gap = !getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim() && hdr ? Math.max(0, hdr.offsetHeight) + 12 + 'px' : '';
    root.style.scrollMarginTop = gap;
    var target = document.getElementById('finder');
    if (target) target.style.scrollMarginTop = gap;
    root.scrollIntoView({ block: 'start', behavior: smooth && !reduce ? 'smooth' : 'auto' });
    // openSlot put focus in the sheet; moving it to the summary would close the sheet (the document-level focus-out handler).
    if (open && PHONE.matches) return;
    var sum = (open || focusSlot || root.querySelector('.finder-slot[data-facet="players"]') || root).querySelector('[data-slot]');
    if (sum) sum.focus({ preventScroll: true });
  }
  restoreFromHash();
  // BG-31 hero "Find your game" tile: same-page #finder links scroll to the finder and open the row's slot.
  // Modified clicks, and pages without a base-mode finder, keep the browser's own jump.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a[href="#finder"]');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!document.querySelector(ROOT_SELECTOR + '[data-finder-base]')) return;
    e.preventDefault();
    if (location.hash !== '#finder') history.pushState(null, '', '#finder');
    restoreFromHash(link.getAttribute('data-finder-open'), true);
  });
  window.addEventListener('hashchange', function () { restoreFromHash(null, true); });
})();
